import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Link as RouterLink, Navigate } from "react-router-dom";

import DistributionReviewApi from "../../../../infrastructure/api/distributionReview";
import { canAccessDistributionPortal } from "../../../utils/authRoleHelpers";
import DistributionCompanySelect, {
  getSelectedDistributionCompanyId,
  isSystemAdminContext,
} from "../DistributionCompanySelect";
import EDCBreadcrumbs, { EDC_ROOT_CRUMB } from "../EDCBreadcrumbs";
import StatCard from "../EDCStatCard";
import { REQUEST_UNITS, requestViewPath } from "../edcPaths";
import { pick } from "../useDistributionPortalContext";

const hasUnitAccess = (context, unitKey) => {
  if (!context) return true;
  if (pick(context, "isCompanyAdmin", "IsCompanyAdmin") === true) return true;
  return unitKey === "engineering"
    ? pick(context, "canEngineeringReview", "CanEngineeringReview") === true
    : pick(context, "canAreaReview", "CanAreaReview") === true;
};

const formatDays = (days) => {
  if (days == null) return "—";
  if (days < 1) return "کمتر از یک روز";
  return `${Math.floor(days)} روز`;
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("fa-IR");
  } catch {
    return "—";
  }
};

const decisionChip = (review, unitKey) => {
  const decision =
    unitKey === "engineering"
      ? pick(review, "engineeringDecision", "EngineeringDecision")
      : pick(review, "areaDecision", "AreaDecision");
  if (Number(decision) === 1) return <Chip size="small" color="success" label="تأیید" />;
  if (Number(decision) === 2) return <Chip size="small" color="error" label="رد" />;
  return <Chip size="small" label="در انتظار" />;
};

const DistributionUnitDashboardPage = ({ unitKey }) => {
  const unit = REQUEST_UNITS[unitKey];
  const [companyId, setCompanyId] = useState(getSelectedDistributionCompanyId);
  const [context, setContext] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const ctxRes = await DistributionReviewApi.getPortalContext(companyId);
      const ctx = ctxRes?.data ?? ctxRes?.Data ?? null;
      setContext(ctx);
      if (!hasUnitAccess(ctx, unitKey)) {
        setDashboard(null);
        return;
      }
      const res = await DistributionReviewApi.getUnitDashboard({
        unit: unitKey,
        distributionCompanyId: isSystemAdminContext(ctx)
          ? pick(ctx, "distributionCompanyId", "DistributionCompanyId") ?? null
          : null,
      });
      setDashboard(res?.data ?? res?.Data ?? null);
    } catch (e) {
      setError(e?.message || "خطا در دریافت داشبورد کارتابل");
    } finally {
      setLoading(false);
    }
  }, [companyId, unitKey]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canAccessDistributionPortal()) return <Navigate to="/403" replace />;

  const unitAllowed = hasUnitAccess(context, unitKey);
  const companyName = pick(context, "distributionCompanyName", "DistributionCompanyName");
  const portalReviewEnabled = Boolean(
    pick(context, "portalReviewEnabled", "PortalReviewEnabled")
  );
  const overdueDays = pick(dashboard, "overdueThresholdDays", "OverdueThresholdDays") ?? 3;
  const pendingCount = pick(dashboard, "pendingCount", "PendingCount");
  const overdueCount = pick(dashboard, "pendingOverdueCount", "PendingOverdueCount") ?? 0;
  const areas = pick(dashboard, "areas", "Areas") || [];
  const oldestPending = pick(dashboard, "oldestPending", "OldestPending") || [];
  const recentDecisions = pick(dashboard, "recentDecisions", "RecentDecisions") || [];
  const isCompanyWide = pick(dashboard, "isCompanyWide", "IsCompanyWide") === true;
  const decidedByMe = pick(dashboard, "decidedByMeCount", "DecidedByMeCount");
  const approvedByMe = pick(dashboard, "approvedByMeCount", "ApprovedByMeCount") ?? 0;
  const rejectedByMe = pick(dashboard, "rejectedByMeCount", "RejectedByMeCount") ?? 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <EDCBreadcrumbs
          crumbs={[EDC_ROOT_CRUMB, { title: unit.title, to: unit.basePath }]}
        />
      </Grid>

      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              داشبورد {unit.title}
            </Typography>
            {context ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary">
                  {companyName || "—"}
                </Typography>
                {dashboard ? (
                  <Chip
                    size="small"
                    label={
                      isCompanyWide
                        ? "همه امورهای شرکت"
                        : `امورهای شما: ${areas.map((a) => pick(a, "distributionAreaName", "DistributionAreaName")).join("، ") || "—"}`
                    }
                  />
                ) : null}
              </Stack>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <DistributionCompanySelect context={context} onChange={setCompanyId} />
            <Button variant="outlined" size="small" onClick={load} disabled={loading}>
              به‌روزرسانی
            </Button>
            <Button
              variant="contained"
              size="small"
              component={RouterLink}
              to={requestViewPath(unitKey, "NewRequests")}
            >
              ورود به کارتابل
            </Button>
          </Stack>
        </Stack>
      </Grid>

      {error ? (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      ) : null}

      {context && !unitAllowed ? (
        <Grid item xs={12}>
          <Alert severity="warning">دسترسی {unit.title} برای حساب شما فعال نیست.</Alert>
        </Grid>
      ) : null}

      {context && !portalReviewEnabled ? (
        <Grid item xs={12}>
          <Alert severity="info">
            چرخه کارتابل این شرکت توزیع در سولارنت هنوز فعال نشده است؛ تا زمان فعال‌سازی
            (طبق توافق)، درخواستی به این کارتابل ارجاع نمی‌شود.
          </Alert>
        </Grid>
      ) : null}

      {loading && !dashboard ? (
        <Grid item xs={12}>
          <Typography color="text.secondary">در حال بارگذاری…</Typography>
        </Grid>
      ) : null}

      {dashboard ? (
        <>
          {overdueCount > 0 ? (
            <Grid item xs={12}>
              <Alert severity="warning" icon={<WarningAmberOutlinedIcon />}>
                {overdueCount} پرونده بیش از {overdueDays} روز در انتظار بررسی شماست.
              </Alert>
            </Grid>
          ) : null}

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={AssignmentOutlinedIcon}
              label="درخواست‌های جدید (منتظر تصمیم)"
              value={pendingCount}
              color="warning"
              to={requestViewPath(unitKey, "NewRequests")}
              hint={
                pendingCount > 0
                  ? `قدیمی‌ترین: ${formatDays(pick(dashboard, "oldestPendingDays", "OldestPendingDays"))}`
                  : null
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={SyncOutlinedIcon}
              label="در حال بررسی (مراحل بعد)"
              value={pick(dashboard, "inProcessingCount", "InProcessingCount")}
              color="info"
              to={requestViewPath(unitKey, "InProcessing")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={CheckCircleOutlineIcon}
              label="تأیید شده (نهایی)"
              value={pick(dashboard, "acceptedCount", "AcceptedCount")}
              color="success"
              to={requestViewPath(unitKey, "AcceptedRequests")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={BlockOutlinedIcon}
              label="رد شده"
              value={pick(dashboard, "rejectedCount", "RejectedCount")}
              color="error"
              to={requestViewPath(unitKey, "RejectedRequests")}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={HourglassBottomOutlinedIcon}
              label={`تأخیر بیش از ${overdueDays} روز`}
              value={overdueCount}
              color={overdueCount > 0 ? "error" : "secondary"}
              to={requestViewPath(unitKey, "NewRequests")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={HowToRegOutlinedIcon}
              label="تصمیم‌های ثبت‌شده توسط شما"
              value={decidedByMe}
              color="primary"
              hint={`تأیید ${approvedByMe} / رد ${rejectedByMe} — ۳۰ روز اخیر: ${pick(dashboard, "decidedByMeLast30Days", "DecidedByMeLast30Days") ?? 0}`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={HistoryOutlinedIcon}
              label={isCompanyWide ? "تصمیم‌های ۳۰ روز اخیر (کل شرکت)" : "تصمیم‌های ۳۰ روز اخیر (امورهای شما)"}
              value={pick(dashboard, "decidedLast30Days", "DecidedLast30Days")}
              color="secondary"
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardHeader
                title="قدیمی‌ترین پرونده‌های منتظر تصمیم"
                action={
                  <Button component={RouterLink} to={requestViewPath(unitKey, "NewRequests")} size="small">
                    مشاهده همه
                  </Button>
                }
              />
              <CardContent>
                {oldestPending.length === 0 ? (
                  <Typography color="text.secondary">پرونده‌ای در انتظار تصمیم شما نیست.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {oldestPending.map((item) => {
                      const review = pick(item, "review", "Review") || {};
                      const waiting = pick(item, "waitingDays", "WaitingDays");
                      return (
                        <Stack
                          key={pick(review, "id", "Id")}
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1 }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              PRJ-{pick(review, "referenceId", "ReferenceId")} —{" "}
                              {pick(review, "applicantName", "ApplicantName") || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              امور: {pick(review, "distributionAreaName", "DistributionAreaName") || "—"}
                              {pick(review, "cityName", "CityName") ? ` — ${pick(review, "cityName", "CityName")}` : ""}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            color={waiting != null && waiting >= overdueDays ? "error" : "default"}
                            label={`در انتظار: ${formatDays(waiting)}`}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardHeader title="آخرین تصمیم‌ها" />
              <CardContent>
                {recentDecisions.length === 0 ? (
                  <Typography color="text.secondary">هنوز تصمیمی ثبت نشده است.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {recentDecisions.map((item) => {
                      const review = pick(item, "review", "Review") || {};
                      return (
                        <Stack
                          key={pick(review, "id", "Id")}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1 }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              PRJ-{pick(review, "referenceId", "ReferenceId")} —{" "}
                              {pick(review, "applicantName", "ApplicantName") || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {pick(review, "stageTitle", "StageTitle") || "—"} — {formatDate(pick(review, "updateOn", "UpdateOn"))}
                            </Typography>
                          </Box>
                          {decisionChip(review, unitKey)}
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader title="وضعیت کارتابل به تفکیک امور" />
              <CardContent sx={{ overflowX: "auto" }}>
                {areas.length === 0 ? (
                  <Typography color="text.secondary">امور برای شما تعریف نشده است.</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>امور</TableCell>
                        <TableCell align="center">جدید</TableCell>
                        <TableCell align="center">تأخیر بیش از {overdueDays} روز</TableCell>
                        <TableCell align="center">قدیمی‌ترین انتظار</TableCell>
                        <TableCell align="center">در حال بررسی</TableCell>
                        <TableCell align="center">تأیید شده</TableCell>
                        <TableCell align="center">رد شده</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {areas.map((a) => {
                        const areaOverdue = pick(a, "pendingOverdueCount", "PendingOverdueCount") ?? 0;
                        return (
                          <TableRow key={pick(a, "distributionAreaId", "DistributionAreaId")} hover>
                            <TableCell>{pick(a, "distributionAreaName", "DistributionAreaName")}</TableCell>
                            <TableCell align="center">{pick(a, "pendingCount", "PendingCount") ?? 0}</TableCell>
                            <TableCell align="center">
                              {areaOverdue > 0 ? <Chip size="small" color="error" label={areaOverdue} /> : 0}
                            </TableCell>
                            <TableCell align="center">
                              {formatDays(pick(a, "oldestPendingDays", "OldestPendingDays"))}
                            </TableCell>
                            <TableCell align="center">{pick(a, "inProcessingCount", "InProcessingCount") ?? 0}</TableCell>
                            <TableCell align="center">{pick(a, "acceptedCount", "AcceptedCount") ?? 0}</TableCell>
                            <TableCell align="center">{pick(a, "rejectedCount", "RejectedCount") ?? 0}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};

export default DistributionUnitDashboardPage;
